/** Injected once at boot — all panel/overlay chrome lives here. */

export function injectStyles(): void {
  const style = document.createElement('style');
  style.textContent = `
    :root {
      --panel: rgba(24, 27, 33, 0.92);
      --panel-border: #2c313a;
      --text: #d7dae0;
      --text-dim: #8b93a1;
      --accent: #4c8dd6;
      --accent-soft: rgba(76, 141, 214, 0.18);
      --error: #d66a5e;
      --warn: #d6a45e;
    }

    .nv-toolbar {
      position: absolute; top: 10px; left: 10px; z-index: 20;
      display: flex; gap: 4px; padding: 4px;
      /* Never extend under the right-side panels — wrap instead. */
      flex-wrap: wrap; max-width: calc(100% - 300px);
      background: var(--panel); border: 1px solid var(--panel-border);
      border-radius: 8px;
      -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
    }
    .nv-toolbar .sep { width: 1px; background: var(--panel-border); margin: 2px 3px; }
    .nv-btn {
      display: grid; place-items: center;
      width: 34px; height: 34px; border: none; border-radius: 6px;
      background: transparent; color: var(--text-dim); cursor: pointer;
    }
    .nv-btn:hover { background: rgba(255,255,255,0.06); color: var(--text); }
    .nv-btn.active { background: var(--accent-soft); color: var(--accent); }
    .nv-btn:disabled { opacity: 0.32; cursor: default; }
    .nv-btn:disabled:hover { background: transparent; color: var(--text-dim); }
    .nv-btn svg { width: 18px; height: 18px; }

    .nv-hint {
      position: absolute; top: 58px; left: 12px; z-index: 20;
      font-size: 11.5px; color: var(--text-dim);
      background: var(--panel); border: 1px solid var(--panel-border);
      border-radius: 6px; padding: 4px 9px; pointer-events: none;
      max-width: 340px;
    }

    /* SketchUp-style rubber-band selection box. Solid = window (L→R,
       whole entities only); dashed green = crossing (R→L, anything touched). */
    .nv-marquee {
      position: fixed; z-index: 40; pointer-events: none;
      border: 1px solid #4c8dd6; background: rgba(76, 141, 214, 0.12);
    }
    .nv-marquee.crossing {
      border-style: dashed; border-color: #5bbf7a; background: rgba(91, 191, 122, 0.12);
    }

    .nv-props {
      position: absolute; top: 10px; right: 10px; z-index: 20;
      width: 218px; padding: 10px 12px 12px;
      background: var(--panel); border: 1px solid var(--panel-border);
      border-radius: 8px; color: var(--text); font-size: 12px;
      -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
      user-select: none; -webkit-user-select: none;
    }
    .nv-props h3 {
      margin: 0 0 8px; font-size: 11px; font-weight: 600;
      letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-dim);
    }
    .nv-props .row { display: flex; align-items: center; justify-content: space-between; margin: 6px 0; gap: 8px; }
    .nv-props label { color: var(--text-dim); }
    .nv-props input[type=text], .nv-props input[type=number], .nv-props select {
      width: 104px; padding: 3px 6px; border-radius: 5px;
      border: 1px solid var(--panel-border); background: #14171c; color: var(--text);
      font: inherit;
    }
    .nv-props .actions { display: flex; gap: 6px; margin-top: 10px; }
    .nv-props .actions button {
      flex: 1; padding: 5px 0; border-radius: 6px; border: 1px solid var(--panel-border);
      background: #20242b; color: var(--text); cursor: pointer; font: inherit; font-size: 11.5px;
    }
    .nv-props .actions button:hover { background: #272c35; }
    .nv-props .actions button.danger { color: var(--error); }
    .nv-props .status-error { color: var(--error); margin-top: 6px; }
    .nv-props .status-blocked { color: var(--warn); margin-top: 6px; }

    #timeline-host {
      display: flex; align-items: center; gap: 6px;
      height: 58px; padding: 0 10px;
      background: #14161b; border-top: 1px solid var(--panel-border);
      overflow-x: auto; overflow-y: hidden;
      user-select: none; -webkit-user-select: none;
    }
    .nv-chip {
      display: flex; flex-direction: column; align-items: center; gap: 3px;
      min-width: 52px; padding: 6px 6px 4px; border-radius: 7px;
      border: 1px solid transparent; color: var(--text-dim);
      cursor: pointer; flex-shrink: 0;
    }
    .nv-chip:hover { background: rgba(255,255,255,0.05); }
    .nv-chip.selected { border-color: var(--accent); background: var(--accent-soft); color: var(--text); }
    .nv-chip svg { width: 17px; height: 17px; }
    .nv-chip .nm { font-size: 10px; white-space: nowrap; }
    .nv-chip.err { color: var(--error); }
    .nv-chip.blocked { color: var(--warn); }
    .nv-chip.suppressed { opacity: 0.42; }
    .nv-chip.suppressed .nm { text-decoration: line-through; }
    .nv-chip.future { opacity: 0.3; }
    .nv-rollback {
      width: 7px; align-self: stretch; margin: 8px 0; flex-shrink: 0;
      border-radius: 4px; background: var(--accent); opacity: 0.85;
      cursor: ew-resize; touch-action: none;
    }
    .nv-rollback:hover { opacity: 1; box-shadow: 0 0 6px var(--accent); }
    .nv-tl-empty { color: var(--text-dim); font-size: 11.5px; padding-left: 4px; }

    .nv-side {
      position: absolute; top: 10px; right: 10px; bottom: 10px; z-index: 25;
      width: 264px; padding: 12px 14px; overflow-y: auto;
      background: var(--panel); border: 1px solid var(--panel-border);
      border-radius: 10px; color: var(--text); font-size: 12px;
      -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
    }
    .nv-side h3 {
      margin: 0 0 10px; font-size: 11px; font-weight: 600;
      letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-dim);
    }
    .nv-side .field { margin: 8px 0; }
    .nv-side .field label { display: block; color: var(--text-dim); margin-bottom: 3px; }
    .nv-side input, .nv-side textarea {
      width: 100%; box-sizing: border-box; padding: 5px 7px; border-radius: 5px;
      border: 1px solid var(--panel-border); background: #14171c; color: var(--text);
      font: inherit;
    }
    .nv-side textarea { min-height: 64px; resize: vertical; }
    .nv-primary {
      width: 100%; margin-top: 10px; padding: 7px 0; border-radius: 6px;
      border: none; background: var(--accent); color: #0e1116;
      font: inherit; font-weight: 600; cursor: pointer;
    }
    .nv-primary:disabled { opacity: 0.5; cursor: default; }
    .side-status { margin-top: 10px; color: var(--text-dim); word-break: break-word; }
    .side-caption { margin: 12px 0 4px; color: var(--text-dim); }
    .side-img { width: 100%; border-radius: 6px; border: 1px solid var(--panel-border); }
    .side-search { margin-bottom: 10px; }
    .side-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
    .side-card {
      cursor: pointer; border: 1px solid var(--panel-border); border-radius: 7px;
      overflow: hidden; background: #14171c;
    }
    .side-card:hover { border-color: var(--accent); }
    .side-card img { width: 100%; aspect-ratio: 4/3; object-fit: cover; display: block; }
    .side-card span {
      display: block; padding: 4px 6px; font-size: 10.5px; color: var(--text-dim);
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .proj-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin: 10px 0 12px; }
    .proj-actions button {
      padding: 6px 0; border-radius: 6px; border: 1px solid var(--panel-border);
      background: #20242b; color: var(--text); cursor: pointer; font: inherit; font-size: 11.5px;
    }
    .proj-actions button:hover { background: #272c35; }
    .proj-card {
      display: flex; align-items: center; gap: 9px;
      padding: 6px; margin-bottom: 7px; border-radius: 8px;
      border: 1px solid var(--panel-border); cursor: pointer; background: #171a20;
    }
    .proj-card:hover { border-color: #3d4450; }
    .proj-card.current { border-color: var(--accent); background: var(--accent-soft); cursor: default; }
    .proj-thumb {
      width: 58px; height: 42px; border-radius: 5px; overflow: hidden;
      background: #101318; flex-shrink: 0;
    }
    .proj-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .proj-meta { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    .proj-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .proj-date { color: var(--text-dim); font-size: 10.5px; }
    .proj-del {
      border: none; background: transparent; color: var(--text-dim);
      cursor: pointer; font-size: 12px; padding: 4px;
    }
    .proj-del:hover { color: var(--error); }
    .proj-insert { font-size: 14px; }
    .proj-insert:hover { color: var(--accent) !important; }

    .body-row {
      display: flex; align-items: center; gap: 7px;
      padding: 6px 7px; border-radius: 6px; cursor: pointer;
    }
    .body-row:hover { background: rgba(255,255,255,0.05); }
    .body-row.selected { background: var(--accent-soft); }
    .body-row.suppressed { opacity: 0.45; }
    .body-row svg { width: 15px; height: 15px; flex-shrink: 0; color: var(--text-dim); }
    .body-name { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .body-tris { color: var(--text-dim); font-size: 10.5px; }
    .body-eye {
      border: none; background: transparent; color: var(--text-dim);
      cursor: pointer; font-size: 12px; padding: 0 2px;
    }
    .body-eye:hover { color: var(--text); }

    .nv-chat { display: flex; flex-direction: column; }
    .chat-head { display: flex; align-items: center; gap: 6px; margin-bottom: 8px; }
    .chat-head h3 { flex: 1; margin: 0; }
    .chat-mini {
      border: 1px solid var(--panel-border); background: #20242b; color: var(--text-dim);
      border-radius: 5px; font: inherit; font-size: 10.5px; padding: 2px 8px; cursor: pointer;
    }
    .chat-mini:hover { color: var(--text); }
    .chat-mini.danger:hover { color: var(--error); }
    .chat-log { flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 6px; min-height: 0; }
    .chat-empty { color: var(--text-dim); font-size: 11.5px; line-height: 1.5; padding: 6px 2px; }
    /* Chat content must be copyable — override the panel's user-select:none
       (which exists to stop accidental drag-selection during orbiting). */
    .chat-msg, .chat-tool-call {
      user-select: text; -webkit-user-select: text; cursor: text;
    }
    .chat-msg { padding: 6px 9px; border-radius: 8px; font-size: 12px; white-space: pre-wrap; word-break: break-word; }
    .chat-user { background: var(--accent-soft); align-self: flex-end; max-width: 92%; }
    .chat-assistant { background: #20242b; align-self: flex-start; max-width: 92%; }
    .chat-tool-call {
      color: var(--text-dim); font-size: 10.5px; font-family: ui-monospace, monospace;
      padding: 2px 6px; border-left: 2px solid var(--panel-border);
    }
    .chat-error { background: rgba(214,106,94,0.15); color: var(--error); }
    .chat-busy { color: var(--text-dim); font-style: italic; }
    .chat-composer { display: flex; gap: 6px; margin-top: 8px; }
    .chat-composer textarea {
      flex: 1; resize: none; padding: 6px 8px; border-radius: 6px;
      border: 1px solid var(--panel-border); background: #14171c; color: var(--text); font: inherit;
    }
    .chat-tool-row { display: flex; align-items: center; gap: 6px; padding: 4px 2px; }
    .chat-eye {
      display: flex; align-items: center; gap: 5px; margin-top: 6px;
      color: var(--text-dim); font-size: 10.5px; cursor: pointer; user-select: none;
    }

    .nv-menu {
      position: fixed; z-index: 40; display: flex; flex-direction: column;
      min-width: 170px; padding: 4px;
      background: var(--panel); border: 1px solid var(--panel-border);
      border-radius: 8px;
      -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
    }
    .nv-menu button {
      text-align: left; padding: 6px 10px; border: none; border-radius: 5px;
      background: transparent; color: var(--text); font: inherit; font-size: 12px;
      cursor: pointer;
    }
    .nv-menu button:hover { background: var(--accent-soft); }
    .nv-menu button:disabled { opacity: 0.4; cursor: default; }
    .nv-menu button:disabled:hover { background: transparent; }
    .nv-context button { display: flex; align-items: center; gap: 14px; }
    .nv-context button span:first-child { flex: 1; text-align: left; }
    .ctx-hint { color: var(--text-dim); font-size: 10.5px; }
    .ctx-sep { height: 1px; background: var(--panel-border); margin: 4px 2px; }
    .ctx-header {
      padding: 4px 10px 2px; font-size: 10px; letter-spacing: 0.06em;
      text-transform: uppercase; color: var(--text-dim);
    }

    .nv-iostatus {
      position: absolute; bottom: 40px; left: 50%; transform: translateX(-50%);
      z-index: 30; padding: 5px 12px; border-radius: 6px;
      background: var(--panel); border: 1px solid var(--panel-border);
      color: var(--text); font-size: 12px; pointer-events: none;
    }

    .nv-snap-badge {
      position: absolute; z-index: 30; pointer-events: none;
      padding: 1px 6px; border-radius: 4px;
      background: rgba(20, 23, 28, 0.85); border: 1px solid var(--panel-border);
      color: var(--text); font-size: 10.5px; white-space: nowrap;
    }

    .nv-dim {
      position: absolute; z-index: 30; pointer-events: none;
      background: var(--panel); border: 1px solid var(--accent);
      border-radius: 5px; padding: 2px 7px;
      font-size: 11.5px; color: var(--text); white-space: nowrap;
    }
    .nv-dim input {
      pointer-events: auto; width: 76px; border: none; background: transparent;
      color: var(--accent); font: inherit; outline: none;
    }

    .nv-measure {
      position: absolute; z-index: 30; pointer-events: none;
      display: flex; flex-direction: column; gap: 1px;
      padding: 4px 9px; border-radius: 6px;
      background: var(--panel); border: 1px solid #e8c66a;
      color: var(--text); font-size: 12px; white-space: nowrap;
    }
    .nv-measure span { color: var(--text-dim); font-size: 10.5px; }

    .nv-section {
      position: absolute; bottom: 14px; left: 50%; transform: translateX(-50%);
      z-index: 26; display: flex; align-items: center; gap: 7px;
      padding: 6px 10px; border-radius: 8px;
      background: var(--panel); border: 1px solid var(--panel-border);
      -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px);
      color: var(--text); font-size: 11.5px;
    }
    .nv-section .sec-title { color: var(--text-dim); text-transform: uppercase; font-size: 10px; letter-spacing: 0.06em; }
    .nv-section .sec-axis {
      width: 26px; height: 24px; border-radius: 5px; border: 1px solid var(--panel-border);
      background: #20242b; color: var(--text-dim); font: inherit; cursor: pointer;
    }
    .nv-section .sec-axis.active { background: var(--accent-soft); color: var(--accent); border-color: var(--accent); }
    .nv-section input[type=range] { width: 180px; accent-color: var(--accent); }
    .nv-section .sec-offset {
      width: 58px; padding: 3px 6px; border-radius: 5px;
      border: 1px solid var(--panel-border); background: #14171c; color: var(--text); font: inherit;
    }

    .param-row {
      display: flex; align-items: center; gap: 6px; margin: 6px 0;
    }
    .param-row .param-name {
      width: 64px; flex-shrink: 0; overflow: hidden; text-overflow: ellipsis;
      white-space: nowrap; color: var(--text);
    }
    .param-row input { flex: 1; min-width: 0; }
    .param-row .param-name-input { width: 64px; flex: 0 0 64px; }
    .param-row .param-value { color: var(--text-dim); font-size: 10.5px; white-space: nowrap; }
    .param-row .param-value.err { color: var(--error); cursor: help; }
    .param-add { margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--panel-border); }

    .constraint-row {
      display: flex; align-items: center; gap: 6px; margin: 4px 0;
      font-size: 10.5px; color: var(--text-dim);
    }
    .constraint-row span { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

    .nv-sketch-dims { position: absolute; inset: 0; z-index: 28; pointer-events: none; }
    .nv-sketch-dim, .nv-sketch-glyph {
      position: absolute; transform: translate(-50%, -130%);
      pointer-events: auto; cursor: pointer; white-space: nowrap;
      border-radius: 4px; padding: 1px 6px; font-size: 10.5px;
      background: rgba(20, 23, 28, 0.88); border: 1px solid var(--accent);
      color: var(--accent);
    }
    .nv-sketch-glyph {
      border-color: var(--panel-border); color: var(--text-dim);
      padding: 0 4px; font-size: 10px;
    }
    .nv-sketch-dim:hover { background: var(--accent-soft); }
    .nv-sketch-glyph:hover { color: var(--error); border-color: var(--error); }

    .nv-drawing {
      position: absolute; inset: 0; z-index: 42;
      display: flex; flex-direction: column;
      background: rgba(14, 16, 20, 0.96);
      -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
    }
    .drawing-bar {
      display: flex; align-items: center; gap: 8px; padding: 10px 14px;
      color: var(--text); font-size: 12.5px;
      border-bottom: 1px solid var(--panel-border);
    }
    .drawing-bar span { flex: 1; }
    .drawing-bar button {
      padding: 5px 14px; border-radius: 6px; border: 1px solid var(--panel-border);
      background: #20242b; color: var(--text); cursor: pointer; font: inherit; font-size: 11.5px;
    }
    .drawing-bar button:hover { background: #272c35; }
    .drawing-bar button.primary { background: var(--accent); color: #0e1116; border: none; }
    .drawing-bar button.active-mode { background: var(--accent-soft); color: var(--accent); border-color: var(--accent); }
    .drawing-bar select {
      padding: 4px 6px; border-radius: 6px; border: 1px solid var(--panel-border);
      background: #20242b; color: var(--text); font: inherit; font-size: 11.5px;
    }
    .drawing-sheet {
      flex: 1; display: flex; align-items: center; justify-content: center;
      padding: 16px; min-height: 0; position: relative; flex-direction: column;
    }
    .drawing-sheet svg {
      max-width: 100%; max-height: calc(100% - 24px);
      box-shadow: 0 8px 40px rgba(0,0,0,0.5); border-radius: 2px;
    }
    .drawing-status { color: var(--text-dim); font-size: 13px; }
    .drawing-note { color: var(--text-dim); font-size: 11px; margin-top: 8px; }

    .nv-cheatsheet {
      position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
      z-index: 45; width: 420px; max-height: 80%; overflow-y: auto;
      padding: 14px 18px; border-radius: 10px;
      background: var(--panel); border: 1px solid var(--panel-border);
      -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px);
      color: var(--text); font-size: 12px;
    }
    .nv-cheatsheet h3 {
      display: flex; justify-content: space-between; margin: 0 0 6px;
      font-size: 12px; letter-spacing: 0.05em; text-transform: uppercase; color: var(--text);
    }
    .nv-cheatsheet .cheat-close { cursor: pointer; color: var(--text-dim); }
    .nv-cheatsheet .cheat-close:hover { color: var(--error); }
    .nv-cheatsheet h4 {
      margin: 12px 0 4px; font-size: 10.5px; letter-spacing: 0.06em;
      text-transform: uppercase; color: var(--accent);
    }
    .nv-cheatsheet .cheat-row { display: flex; gap: 10px; padding: 2px 0; }
    .nv-cheatsheet kbd {
      flex: 0 0 150px; font-family: ui-monospace, monospace; font-size: 10.5px;
      color: var(--text); background: #14171c; border: 1px solid var(--panel-border);
      border-radius: 4px; padding: 1px 6px; white-space: nowrap;
      overflow: hidden; text-overflow: ellipsis;
    }
    .nv-cheatsheet span { color: var(--text-dim); }

    .nv-props input.has-expr { color: #7ec8a8; font-family: ui-monospace, monospace; font-size: 11px; }
    .nv-props input.expr-error { border-color: var(--error); color: var(--error); }
  `;
  document.head.appendChild(style);
}
